import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Card, EmptyState, SectionTitle, Badge, Money, formatDate, formatWindow } from "@/components/dash";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ORDER_STATUSES, PAYMENT_STATUSES, STATUS_LABEL } from "@/lib/constants";
import { ClipboardList, Search, Printer, Loader2, Plus, Minus, Trash2, ChevronRight, CalendarClock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [days, setDays] = useState([]);
  const [products, setProducts] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dayFilter, setDayFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [connect, setConnect] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (search) params.search = search;
    if (statusFilter !== "all") params.status = statusFilter;
    if (dayFilter !== "all") params.bake_day_id = dayFilter;
    setLoading(true);
    api.get("/orders", { params }).then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, [search, statusFilter, dayFilter]);

  useEffect(() => { api.get("/bake-days").then((r) => setDays(r.data)); api.get("/products").then((r) => setProducts(r.data.filter((p) => p.active))); api.get("/recipes").then((r) => setRecipes(r.data)); api.get("/payments/connect/status").then((r) => setConnect(r.data)).catch(() => setConnect({ charges_enabled: false })); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const dayTitle = (id) => days.find((d) => d.id === id)?.title || "—";

  return (
    <div>
      <SectionTitle action={
        <div className="flex gap-2">
          <Button onClick={() => setManualOpen(true)} className="rounded-full bg-terracotta text-warmwhite hover:bg-terracotta/90" data-testid="add-manual-order-btn"><Plus className="mr-1.5 h-4 w-4" /> Add order</Button>
          <Button variant="outline" onClick={() => window.print()} className="rounded-full border-cocoa/20 text-cocoa" data-testid="print-orders-btn"><Printer className="mr-1.5 h-4 w-4" /> Print list</Button>
        </div>
      }>Orders</SectionTitle>

      <div className="mb-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-cocoa/40" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or order #" className="rounded-full bg-warmwhite pl-9" data-testid="order-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 rounded-full bg-warmwhite" data-testid="order-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={dayFilter} onValueChange={setDayFilter}>
          <SelectTrigger className="w-44 rounded-full bg-warmwhite" data-testid="order-day-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All bake days</SelectItem>{days.filter((d) => !d.is_custom).map((d) => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-cocoa/5" />)}</div>
      ) : orders.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No orders found" body="Orders placed through your storefront will appear here, ready to manage." />
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const itemCount = o.items.reduce((s, i) => s + i.quantity, 0);
            const oDay = days.find((d) => d.id === o.bake_day_id);
            const oWin = oDay?.pickup_windows?.find((w) => w.id === o.pickup_window_id);
            return (
            <Card key={o.id} onClick={() => setActive(o)} className="group cursor-pointer p-5 transition-all hover:-translate-y-0.5 hover:border-terracotta/30 hover:shadow-[0_8px_28px_rgba(64,53,47,0.10)]" data-testid={`order-row-${o.order_number}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-medium text-cocoa">#{o.order_number} · {o.customer_name}</p>
                  <p className="text-sm text-cocoa/60">{dayTitle(o.bake_day_id)} · {itemCount} item{itemCount !== 1 ? "s" : ""} · <Money value={o.subtotal} /></p>
                  {oDay && <p className="mt-0.5 flex items-center gap-1 text-xs text-cocoa/45"><CalendarClock className="h-3 w-3" /> Pickup {formatDate(oDay.pickup_date)}{oWin ? ` · ${formatWindow(oWin)}` : ""}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge status={o.status} /><Badge status={o.payment_status} />
                  <ChevronRight className="h-5 w-5 text-cocoa/30 transition-transform group-hover:translate-x-0.5 group-hover:text-terracotta" />
                </div>
              </div>
            </Card>
            );
          })}
        </div>
      )}

      {active && <OrderDialog order={active} day={days.find((d) => d.id === active.bake_day_id)} dayTitle={dayTitle(active.bake_day_id)} connect={connect} onClose={() => setActive(null)} onUpdate={(u) => { setActive(u); load(); }} onDeleted={() => { setActive(null); load(); }} />}
      {manualOpen && <ManualOrderDialog days={days.filter((d) => d.status !== "completed" && !d.is_custom)} products={products} recipes={recipes} onClose={() => setManualOpen(false)} onSaved={() => { setManualOpen(false); load(); api.get("/bake-days").then((r) => setDays(r.data)); }} />}
    </div>
  );
}

function OrderDialog({ order, day, dayTitle, connect, onClose, onUpdate, onDeleted }) {
  const [notes, setNotes] = useState(order.internal_notes || "");
  const [saving, setSaving] = useState(false);
  const [dep, setDep] = useState({ amount: "", date: new Date().toISOString().slice(0, 10), method: "Cash" });
  const [depBusy, setDepBusy] = useState(false);
  const total = order.total ?? order.subtotal;
  const win = day?.pickup_windows?.find((w) => w.id === order.pickup_window_id);

  const recordDeposit = async () => {
    const amt = parseFloat(dep.amount);
    if (!amt || amt <= 0) return toast.error("Enter a deposit amount");
    setDepBusy(true);
    try { const { data } = await api.post(`/orders/${order.id}/deposit`, { amount: amt, date: dep.date, method: dep.method }); onUpdate(data); toast.success("Deposit recorded"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setDepBusy(false); }
  };
  const sendDepositLink = async () => {
    const amt = parseFloat(dep.amount);
    if (!amt || amt <= 0) return toast.error("Enter a deposit amount");
    setDepBusy(true);
    try {
      const { data } = await api.post(`/payments/deposit-checkout`, { order_id: order.id, amount: amt, origin_url: window.location.origin });
      try { await navigator.clipboard.writeText(data.checkout_url); } catch (err) { console.debug("Clipboard copy skipped:", err); }
      window.open(data.checkout_url, "_blank");
      toast.success("Deposit link created, emailed to the customer & copied to your clipboard");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setDepBusy(false); }
  };
  const removeDeposit = async () => {
    setDepBusy(true);
    try { const { data } = await api.delete(`/orders/${order.id}/deposit`); onUpdate(data); toast.success("Deposit removed"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setDepBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(`Permanently delete order #${order.order_number}? Inventory will be restored.`)) return;
    try {
      await api.delete(`/orders/${order.id}`);
      toast.success("Order deleted & inventory restored");
      onDeleted();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const patch = async (payload, closeAfter = false) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/orders/${order.id}`, payload);
      if (closeAfter) { toast.success("Order canceled & inventory restored"); onClose(); onUpdate(data); }
      else { onUpdate(data); toast.success("Order updated"); }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-warmwhite">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-cocoa">Order #{order.order_number}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="rounded-xl bg-cream p-4 text-sm">
            <p className="font-medium text-cocoa">{order.customer_name}</p>
            <p className="text-cocoa/60">{order.customer_email}</p>
            {order.customer_phone && <p className="text-cocoa/60">{order.customer_phone}</p>}
            {order.notes && <p className="mt-2 italic text-cocoa/60">"{order.notes}"</p>}
          </div>

          <div className="rounded-xl border border-cocoa/10 bg-warmwhite p-4 text-sm" data-testid="order-pickup-details">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-terracotta">Pickup details</p>
            <p className="text-cocoa">{dayTitle}</p>
            {day && <p className="text-cocoa/70">{formatDate(day.pickup_date)}{win ? ` · ${formatWindow(win)}` : ""}</p>}
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-cocoa">Items</p>
            <div className="space-y-1.5">
              {order.items.map((i, idx) => (
                <div key={idx} className="flex justify-between text-sm"><span className="text-cocoa/70">{i.name} × {i.quantity}</span><span className="text-cocoa"><Money value={i.price * i.quantity} /></span></div>
              ))}
              {order.discount > 0 && (
                <>
                  <div className="flex justify-between text-sm text-cocoa/60"><span>Subtotal</span><span><Money value={order.subtotal} /></span></div>
                  <div className="flex justify-between text-sm text-green-700"><span>Discount ({order.promo_code})</span><span>-<Money value={order.discount} /></span></div>
                </>
              )}
              <div className="flex justify-between border-t border-cocoa/10 pt-2 text-sm font-semibold text-cocoa"><span>Total</span><span><Money value={order.total ?? order.subtotal} /></span></div>
            </div>
          </div>

          {order.status !== "canceled" && order.payment_status !== "paid" && (
            <div className="rounded-xl border border-cocoa/10 bg-warmwhite p-4" data-testid="deposit-section">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-terracotta">Deposit</p>
              {order.deposit_paid ? (
                <div className="text-sm" data-testid="deposit-summary">
                  <div className="flex items-center justify-between"><span className="text-cocoa">Deposit paid</span><span className="font-semibold text-cocoa"><Money value={order.deposit_amount} /></span></div>
                  <p className="mt-0.5 text-xs text-cocoa/55">{order.deposit_source === "stripe" ? "Paid by card through Oven & Order" : `Recorded manually · ${order.deposit_method}`}{order.deposit_date ? ` · ${formatDate(order.deposit_date)}` : ""}</p>
                  <div className="mt-1.5 flex items-center justify-between border-t border-cocoa/10 pt-1.5"><span className="text-cocoa/70">Balance due at pickup</span><span className="font-semibold text-cocoa"><Money value={order.balance_due ?? (total - (order.deposit_amount || 0))} /></span></div>
                  {order.deposit_source !== "stripe" && <Button variant="ghost" size="sm" onClick={removeDeposit} disabled={depBusy} className="mt-2 rounded-full text-destructive hover:bg-destructive/10" data-testid="remove-deposit-btn">Remove deposit</Button>}
                </div>
              ) : (
                <div className="space-y-2">
                  <div><Label className="text-xs text-cocoa/60">Deposit amount</Label><Input type="number" step="0.01" min="0" value={dep.amount} onChange={(e) => setDep({ ...dep, amount: e.target.value })} placeholder={`e.g. ${(total * 0.5).toFixed(2)}`} className="mt-1 rounded-xl bg-cream" data-testid="deposit-amount" /></div>
                  {connect?.charges_enabled ? (
                    <>
                      <Button onClick={sendDepositLink} disabled={depBusy} className="w-full rounded-full bg-terracotta text-warmwhite hover:bg-terracotta/90" data-testid="send-deposit-link-btn">{depBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send card deposit link"}</Button>
                      <p className="text-center text-[11px] text-cocoa/45">Emails a secure Stripe link to the customer — marked paid automatically once they pay.</p>
                      <p className="text-center text-[11px] uppercase tracking-wide text-cocoa/35">or record a payment you took yourself</p>
                    </>
                  ) : (
                    <p className="text-[11px] text-cocoa/50">Connect Stripe in Settings to charge card deposits automatically. For now you can record a deposit you collected yourself.</p>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs text-cocoa/60">Date</Label><Input type="date" value={dep.date} onChange={(e) => setDep({ ...dep, date: e.target.value })} className="mt-1 rounded-xl bg-cream" data-testid="deposit-date" /></div>
                    <div><Label className="text-xs text-cocoa/60">Method</Label>
                      <Select value={dep.method} onValueChange={(v) => setDep({ ...dep, method: v })}>
                        <SelectTrigger className="mt-1 rounded-xl bg-cream" data-testid="deposit-method"><SelectValue /></SelectTrigger>
                        <SelectContent>{["Cash", "Venmo", "Zelle", "PayPal", "Bank transfer", "Other"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button variant="outline" onClick={recordDeposit} disabled={depBusy} className="w-full rounded-full border-cocoa/20 text-cocoa" data-testid="record-deposit-btn">{depBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record deposit manually"}</Button>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-cocoa">Order status</Label>
              <Select value={order.status} onValueChange={(v) => patch({ status: v })}>
                <SelectTrigger className="mt-1.5 rounded-xl bg-cream" data-testid="order-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-cocoa">Payment status</Label>
              <Select value={order.payment_status} onValueChange={(v) => patch({ payment_status: v })}>
                <SelectTrigger className="mt-1.5 rounded-xl bg-cream" data-testid="order-payment-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-cocoa">Internal notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 rounded-xl bg-cream" rows={2} data-testid="order-internal-notes" />
            <Button onClick={() => patch({ internal_notes: notes })} disabled={saving} variant="outline" size="sm" className="mt-2 rounded-full border-cocoa/20 text-cocoa" data-testid="save-notes-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save notes"}</Button>
          </div>

          {order.status !== "canceled" && (
            <Button variant="ghost" onClick={() => { if (window.confirm("Cancel this order and restore inventory?")) patch({ status: "canceled" }, true); }} className="w-full rounded-full text-destructive hover:bg-destructive/10" data-testid="cancel-order-btn">Cancel order &amp; restore inventory</Button>
          )}
          <Button variant="ghost" onClick={del} className="w-full rounded-full text-destructive hover:bg-destructive/10" data-testid="delete-order-btn"><Trash2 className="mr-1.5 h-4 w-4" /> Delete order</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function ManualOrderDialog({ days, products, recipes, onClose, onSaved }) {
  const [mode, setMode] = useState("bakeday");
  const [dayId, setDayId] = useState(days[0]?.id || "");
  const [windowId, setWindowId] = useState("");
  const [qty, setQty] = useState({});
  const [pickupDate, setPickupDate] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [customItems, setCustomItems] = useState([{ name: "", price: "", quantity: 1, recipe_id: "none" }]);
  const [info, setInfo] = useState({ customer_name: "", customer_email: "", customer_phone: "", notes: "" });
  const [promoCode, setPromoCode] = useState("");
  const [markPaid, setMarkPaid] = useState(false);
  const [saving, setSaving] = useState(false);

  const day = days.find((d) => d.id === dayId);
  const dayProducts = (day?.products || []).map((bp) => ({ ...bp, product: products.find((p) => p.id === bp.product_id) })).filter((x) => x.product);
  const items = dayProducts.filter((p) => qty[p.product_id] > 0);
  const customValid = customItems.filter((c) => c.name.trim() && c.quantity > 0);
  const subtotal = mode === "custom"
    ? customValid.reduce((s, c) => s + (parseFloat(c.price) || 0) * c.quantity, 0)
    : items.reduce((s, p) => s + p.product.price * qty[p.product_id], 0);
  const totalCount = mode === "custom"
    ? customValid.reduce((s, c) => s + c.quantity, 0)
    : items.reduce((s, p) => s + qty[p.product_id], 0);

  useEffect(() => { setWindowId(day?.pickup_windows?.[0]?.id || ""); setQty({}); }, [dayId]); // eslint-disable-line react-hooks/exhaustive-deps

  const step = (id, delta) => setQty((q) => ({ ...q, [id]: Math.max(0, (q[id] || 0) + delta) }));
  const setCI = (i, k, v) => setCustomItems((cs) => cs.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const addCI = () => setCustomItems((cs) => [...cs, { name: "", price: "", quantity: 1, recipe_id: "none" }]);
  const rmCI = (i) => setCustomItems((cs) => cs.filter((_, idx) => idx !== i));
  const linkRecipe = (i, rid) => setCustomItems((cs) => cs.map((c, idx) => {
    if (idx !== i) return c;
    const rec = recipes.find((r) => r.id === rid);
    return { ...c, recipe_id: rid, name: c.name.trim() ? c.name : (rec ? rec.name : "") };
  }));

  const save = async () => {
    if (!info.customer_name.trim()) return toast.error("Enter a customer name");
    setSaving(true);
    try {
      if (mode === "custom") {
        if (!pickupDate) { setSaving(false); return toast.error("Choose a pickup date"); }
        if (customValid.length === 0) { setSaving(false); return toast.error("Add at least one item"); }
        await api.post("/orders/custom", {
          pickup_date: pickupDate, pickup_time: pickupTime, title: "",
          customer_name: info.customer_name, customer_email: info.customer_email || null,
          customer_phone: info.customer_phone, notes: info.notes, mark_paid: markPaid,
          items: customValid.map((c) => ({ name: c.name.trim(), price: parseFloat(c.price) || 0, quantity: c.quantity, recipe_id: c.recipe_id === "none" ? null : c.recipe_id })),
        });
      } else {
        if (!dayId) { setSaving(false); return toast.error("Choose a bake day"); }
        if (!windowId) { setSaving(false); return toast.error("Choose a pickup window"); }
        if (items.length === 0) { setSaving(false); return toast.error("Add at least one item"); }
        await api.post("/orders/manual", {
          bake_day_id: dayId, pickup_window_id: windowId,
          customer_name: info.customer_name, customer_email: info.customer_email || null,
          customer_phone: info.customer_phone, notes: info.notes, mark_paid: markPaid,
          promo_code: promoCode.trim(),
          items: items.map((p) => ({ product_id: p.product_id, quantity: qty[p.product_id] })),
        });
      }
      toast.success("Order added");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not add order"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-warmwhite">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-cocoa">Add an order</DialogTitle></DialogHeader>
        <div className="mb-1 flex gap-1 rounded-full bg-cream p-1" data-testid="order-mode-toggle">
          <button type="button" onClick={() => setMode("bakeday")} className={`flex-1 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${mode === "bakeday" ? "bg-terracotta text-warmwhite" : "text-cocoa/70"}`} data-testid="mode-bakeday">Bake day order</button>
          <button type="button" onClick={() => setMode("custom")} className={`flex-1 rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${mode === "custom" ? "bg-terracotta text-warmwhite" : "text-cocoa/70"}`} data-testid="mode-custom">Custom order</button>
        </div>

        <div className="space-y-4">
          {mode === "bakeday" && days.length === 0 && (
            <p className="py-6 text-center text-sm text-cocoa/50">No active bake day yet — switch to a <button type="button" onClick={() => setMode("custom")} className="text-terracotta underline">custom order</button> to take an off-menu order.</p>
          )}

          {mode === "bakeday" && days.length > 0 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-cocoa">Bake day</Label>
                  <Select value={dayId} onValueChange={setDayId}>
                    <SelectTrigger className="mt-1.5 rounded-xl bg-cream" data-testid="manual-day"><SelectValue /></SelectTrigger>
                    <SelectContent>{days.map((d) => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-cocoa">Pickup window</Label>
                  <Select value={windowId} onValueChange={setWindowId}>
                    <SelectTrigger className="mt-1.5 rounded-xl bg-cream" data-testid="manual-window"><SelectValue placeholder="Choose" /></SelectTrigger>
                    <SelectContent>{(day?.pickup_windows || []).map((w) => <SelectItem key={w.id} value={w.id}>{formatWindow(w)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-cocoa">Items</Label>
                <div className="mt-1.5 space-y-2">
                  {dayProducts.length === 0 && <p className="text-sm text-cocoa/50">This bake day has no products.</p>}
                  {dayProducts.map((p) => (
                    <div key={p.product_id} className="flex items-center justify-between rounded-xl bg-cream px-3 py-2">
                      <span className="text-sm text-cocoa">{p.product.name} <span className="text-cocoa/40">· ${p.product.price.toFixed(2)}</span>{p.remaining <= 0 && <span className="ml-1 text-xs text-terracotta">sold out</span>}</span>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" onClick={() => step(p.product_id, -1)} className="h-7 w-7 rounded-full text-cocoa" data-testid={`manual-minus-${p.product_id}`}><Minus className="h-3.5 w-3.5" /></Button>
                        <span className="w-6 text-center text-sm text-cocoa" data-testid={`manual-qty-${p.product_id}`}>{qty[p.product_id] || 0}</span>
                        <Button variant="ghost" size="icon" onClick={() => step(p.product_id, 1)} className="h-7 w-7 rounded-full text-cocoa" data-testid={`manual-plus-${p.product_id}`}><Plus className="h-3.5 w-3.5" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div><Label className="text-cocoa">Promo code (optional)</Label><Input value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} placeholder="e.g. SAVE10" className="mt-1.5 rounded-xl bg-cream uppercase" data-testid="manual-promo" /></div>
            </>
          )}

          {mode === "custom" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-cocoa">Pickup date *</Label><Input type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} className="mt-1.5 rounded-xl bg-cream" data-testid="custom-date" /></div>
                <div><Label className="text-cocoa">Pickup time</Label><Input type="time" value={pickupTime} onChange={(e) => setPickupTime(e.target.value)} className="mt-1.5 rounded-xl bg-cream" data-testid="custom-time" /></div>
              </div>
              <p className="-mt-1 text-xs text-cocoa/50">Saving creates a private bake day (hidden from your storefront) to hold this order.</p>
              <div>
                <Label className="text-cocoa">Items</Label>
                <div className="mt-1.5 space-y-3">
                  {customItems.map((c, i) => {
                    const rec = recipes.find((r) => r.id === c.recipe_id);
                    return (
                      <div key={i} className="rounded-xl bg-cream p-3" data-testid={`custom-item-${i}`}>
                        <div className="flex gap-2">
                          <Input value={c.name} onChange={(e) => setCI(i, "name", e.target.value)} placeholder="Item name (e.g. Custom birthday cake)" className="flex-1 rounded-xl bg-warmwhite" data-testid={`custom-name-${i}`} />
                          <Input type="number" step="0.01" min="0" value={c.price} onChange={(e) => setCI(i, "price", e.target.value)} placeholder="Price" className="w-24 rounded-xl bg-warmwhite" data-testid={`custom-price-${i}`} />
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" onClick={() => setCI(i, "quantity", Math.max(1, c.quantity - 1))} className="h-8 w-8 rounded-full text-cocoa" data-testid={`custom-minus-${i}`}><Minus className="h-3.5 w-3.5" /></Button>
                            <span className="w-6 text-center text-sm text-cocoa" data-testid={`custom-qty-${i}`}>{c.quantity}</span>
                            <Button variant="ghost" size="icon" onClick={() => setCI(i, "quantity", c.quantity + 1)} className="h-8 w-8 rounded-full text-cocoa" data-testid={`custom-plus-${i}`}><Plus className="h-3.5 w-3.5" /></Button>
                          </div>
                          {customItems.length > 1 && <Button variant="ghost" size="icon" onClick={() => rmCI(i)} className="h-8 w-8 rounded-full text-cocoa/40" data-testid={`custom-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <Select value={c.recipe_id} onValueChange={(v) => linkRecipe(i, v)}>
                            <SelectTrigger className="rounded-xl bg-warmwhite" data-testid={`custom-recipe-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="none">No recipe attached</SelectItem>{recipes.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} (yields {r.yield_quantity} {r.yield_unit})</SelectItem>)}</SelectContent>
                          </Select>
                          <span className="whitespace-nowrap text-xs text-cocoa/50">{rec ? "Counts toward totals" : "No recipe attached"}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Button variant="outline" size="sm" onClick={addCI} className="mt-2 rounded-full border-cocoa/20 text-cocoa" data-testid="custom-add-item"><Plus className="mr-1 h-3.5 w-3.5" /> Add item</Button>
              </div>
            </>
          )}

          {(mode === "custom" || days.length > 0) && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-cocoa">Customer name *</Label><Input value={info.customer_name} onChange={(e) => setInfo({ ...info, customer_name: e.target.value })} className="mt-1.5 rounded-xl bg-cream" data-testid="manual-name" /></div>
                <div><Label className="text-cocoa">Phone</Label><Input value={info.customer_phone} onChange={(e) => setInfo({ ...info, customer_phone: e.target.value })} className="mt-1.5 rounded-xl bg-cream" data-testid="manual-phone" /></div>
              </div>
              <div><Label className="text-cocoa">Email (optional — sends confirmation)</Label><Input type="email" value={info.customer_email} onChange={(e) => setInfo({ ...info, customer_email: e.target.value })} className="mt-1.5 rounded-xl bg-cream" data-testid="manual-email" /></div>
              <div><Label className="text-cocoa">Note</Label><Textarea value={info.notes} onChange={(e) => setInfo({ ...info, notes: e.target.value })} className="mt-1.5 rounded-xl bg-cream" rows={2} data-testid="manual-notes" /></div>

              <div className="flex items-center justify-between rounded-xl bg-cream px-4 py-3">
                <Label className="text-cocoa">Mark as paid</Label>
                <Switch checked={markPaid} onCheckedChange={setMarkPaid} data-testid="manual-mark-paid" />
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-cocoa/60">{totalCount} item{totalCount !== 1 ? "s" : ""}</span>
                <span className="font-serif text-xl text-cocoa" data-testid="manual-total">${subtotal.toFixed(2)}</span>
              </div>
              <Button onClick={save} disabled={saving} className="w-full rounded-full bg-terracotta py-6 text-warmwhite hover:bg-terracotta/90" data-testid="save-manual-order-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add order"}</Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
